<script setup>
import { ref } from 'vue'
import { Head, Link, useForm, router } from '@inertiajs/vue3'
import { ArrowLeft, Upload, Trash2, Image as ImageIcon, Star, Wrench, ChevronRight } from 'lucide-vue-next'
import AdminLayout from '../../Layout/AdminLayout.vue'
import Field from '../../Components/Admin/Field.vue'
import { documentKind } from '../../lib/documents'

defineOptions({ layout: AdminLayout })

const props = defineProps({
  aircraft: { type: Object, required: true },
  client: { type: Object, required: true },
  serviceOrder: { type: Object, default: null },
  categories: { type: Array, required: true },
  documents: { type: Array, required: true },
  photos: { type: Array, required: true },
})

const dados = useForm({
  aircraft: { registration: props.aircraft.registration, model: props.aircraft.model },
})

// Abrir uma OS já cria as etapas padrão e as peças monitoradas — sem elas o
// stepper e o mapa do cliente nascem vazios.
const novaOS = useForm({
  service_order: { number: '', opened_on: '', delivery_forecast: '', technician: '' },
})
const abrirOS = () => novaOS.post(`/admin/aeronaves/${props.aircraft.id}/ordens`)

// O documento sobe junto com o arquivo: o Inertia converte para FormData
// sozinho quando há um File nos dados.
const doc = useForm({
  document: { name: '', category: props.categories[0] ?? 'Relatórios', revised_on: '', highlighted: false },
  scope: props.serviceOrder ? 'os' : 'aeronave',
  file: null,
})
const foto = useForm({ service_photo: { caption: '', taken_on: '' }, image: null })

const salvar = () => dados.patch(`/admin/aeronaves/${props.aircraft.id}`)

const enviarDoc = () =>
  doc.post(`/admin/aeronaves/${props.aircraft.id}/documentos`, {
    forceFormData: true,
    onSuccess: () => doc.reset(),
  })

const enviarFoto = () =>
  foto.post(`/admin/aeronaves/${props.aircraft.id}/fotos`, {
    forceFormData: true,
    onSuccess: () => foto.reset(),
  })

const removerDoc = (d) => {
  if (confirm(`Remover "${d.name}"? O arquivo é apagado do servidor.`)) {
    router.delete(`/admin/documentos/${d.id}`)
  }
}
const removerFoto = (f) => {
  if (confirm(`Remover a foto "${f.caption}"?`)) router.delete(`/admin/fotos/${f.id}`)
}
</script>

<template>
  <Head :title="`${props.aircraft.registration} · Administração`" />

  <Link :href="`/admin/clientes/${props.client.id}`" class="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-up-navy">
    <ArrowLeft class="h-4 w-4" aria-hidden="true" />
    {{ props.client.name }}
  </Link>

  <h1 class="mt-3 text-xl font-semibold tracking-tight text-up-navy">
    {{ props.aircraft.registration }} · {{ props.aircraft.model }}
  </h1>
  <p class="mt-1 text-sm text-slate-500">
    <template v-if="props.serviceOrder">
      {{ props.serviceOrder.number }} · {{ props.serviceOrder.status }}
    </template>
    <template v-else>Sem ordem de serviço aberta.</template>
  </p>

  <div class="mt-6 space-y-6">
    <!-- ================= Ordem de serviço ================= -->
    <section class="rounded-card bg-white p-6 shadow-card">
      <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Ordem de serviço</h2>

      <Link
        v-if="props.serviceOrder"
        :href="`/admin/ordens/${props.serviceOrder.id}`"
        class="mt-4 flex items-center gap-3 rounded-lg border border-slate-100 px-4 py-3.5 transition-colors hover:bg-up-bg"
      >
        <Wrench class="h-5 w-5 shrink-0 text-up-navy" aria-hidden="true" />
        <div class="min-w-0">
          <p class="truncate font-medium text-up-navy">{{ props.serviceOrder.number }}</p>
          <p class="text-xs text-slate-500">{{ props.serviceOrder.status }} · editar etapas, tarefas, componentes, parcelas e histórico</p>
        </div>
        <ChevronRight class="ml-auto h-5 w-5 shrink-0 text-slate-300" aria-hidden="true" />
      </Link>

      <template v-else>
        <p class="mt-1 text-sm text-slate-500">
          Sem ordem de serviço aberta. O portal deste cliente mostra a tela de espera até existir uma.
        </p>
        <form class="mt-5 grid gap-4 sm:grid-cols-2" @submit.prevent="abrirOS">
          <Field id="os-num" v-model="novaOS.service_order.number" label="Número" required hint="Ex.: OS 0249" :error="novaOS.errors.number" />
          <Field id="os-tec" v-model="novaOS.service_order.technician" label="Responsável técnico" hint="Ex.: Equipe UP" />
          <Field id="os-ini" v-model="novaOS.service_order.opened_on" label="Abertura" type="date" hint="Em branco usa hoje." />
          <Field id="os-prev" v-model="novaOS.service_order.delivery_forecast" label="Previsão de entrega" type="date" />
          <div class="sm:col-span-2">
            <button
              type="submit"
              :disabled="novaOS.processing"
              class="rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark disabled:opacity-60"
            >
              {{ novaOS.processing ? 'Abrindo…' : 'Abrir ordem de serviço' }}
            </button>
          </div>
        </form>
      </template>
    </section>

    <form class="rounded-card bg-white p-6 shadow-card" @submit.prevent="salvar">
      <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Dados da aeronave</h2>
      <div class="mt-5 grid gap-4 sm:grid-cols-2">
        <Field id="reg" v-model="dados.aircraft.registration" label="Matrícula" required :error="dados.errors.registration" />
        <Field id="mod" v-model="dados.aircraft.model" label="Modelo" required :error="dados.errors.model" />
      </div>
      <button
        type="submit"
        :disabled="dados.processing"
        class="mt-5 rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark disabled:opacity-60"
      >
        {{ dados.processing ? 'Salvando…' : 'Salvar' }}
      </button>
    </form>

    <!-- ================= Documentos ================= -->
    <section class="rounded-card bg-white p-6 shadow-card">
      <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Documentos</h2>

      <form class="mt-5 grid gap-4 border-b border-slate-100 pb-6 sm:grid-cols-2" @submit.prevent="enviarDoc">
        <Field id="d-name" v-model="doc.document.name" label="Nome do documento" required :error="doc.errors.name" />

        <div>
          <label for="d-cat" class="block text-sm font-medium text-up-navy">Categoria</label>
          <input
            id="d-cat"
            v-model="doc.document.category"
            list="categorias"
            required
            class="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-up-navy outline-none focus:border-up-orange focus:ring-2 focus:ring-up-orange/30"
          >
          <!-- datalist: sugere as categorias existentes sem impedir uma nova. -->
          <datalist id="categorias">
            <option v-for="c in props.categories" :key="c" :value="c" />
          </datalist>
        </div>

        <Field id="d-date" v-model="doc.document.revised_on" label="Data do documento" type="date" hint="Em branco usa a data de hoje." />

        <div>
          <label for="d-scope" class="block text-sm font-medium text-up-navy">Onde aparece</label>
          <select
            id="d-scope"
            v-model="doc.scope"
            class="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-up-navy outline-none focus:border-up-orange focus:ring-2 focus:ring-up-orange/30"
          >
            <option v-if="props.serviceOrder" value="os">{{ props.serviceOrder.number }} · manutenção atual</option>
            <option value="aeronave">Documentos da aeronave (permanente)</option>
          </select>
        </div>

        <div class="sm:col-span-2">
          <label for="d-file" class="block text-sm font-medium text-up-navy">Arquivo</label>
          <input
            id="d-file"
            type="file"
            required
            class="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2 text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-up-navy file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white"
            @input="doc.file = $event.target.files[0]"
          >
          <p v-if="doc.errors.file" class="mt-1 text-xs text-up-orange-dark">{{ doc.errors.file }}</p>
        </div>

        <label class="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
          <input v-model="doc.document.highlighted" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-up-orange focus:ring-up-orange/30">
          Destacar no card "Documentos recentes" da Visão Geral
        </label>

        <div class="sm:col-span-2">
          <button
            type="submit"
            :disabled="doc.processing"
            class="flex items-center gap-2 rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark disabled:opacity-60"
          >
            <Upload class="h-4 w-4" aria-hidden="true" />
            {{ doc.processing ? `Enviando… ${doc.progress?.percentage ?? 0}%` : 'Enviar documento' }}
          </button>
        </div>
      </form>

      <p v-if="!props.documents.length" class="mt-5 text-sm text-slate-500">Nenhum documento nesta aeronave.</p>

      <ul v-else class="mt-5 divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-100">
        <li v-for="d in props.documents" :key="d.id" class="flex items-center gap-3 px-4 py-3.5">
          <component :is="documentKind(d.kind).icon" class="h-5 w-5 shrink-0 text-up-navy" aria-hidden="true" />
          <div class="min-w-0">
            <p class="flex items-center gap-1.5 truncate font-medium text-up-navy">
              {{ d.name }}
              <Star v-if="d.highlighted" class="h-3.5 w-3.5 shrink-0 text-up-orange" aria-label="Em destaque na Visão Geral" />
            </p>
            <p class="mt-0.5 truncate text-xs text-slate-500">
              {{ d.category }} · {{ d.size }} · {{ d.updated_at }}
              <span v-if="d.scope === 'aeronave'"> · permanente</span>
              <span v-if="d.uploadedBy"> · enviado por {{ d.uploadedBy }}</span>
              <span v-if="!d.downloadable" class="text-up-orange"> · sem arquivo</span>
            </p>
          </div>
          <button
            type="button"
            class="ml-auto shrink-0 rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-up-orange"
            :aria-label="`Remover ${d.name}`"
            @click="removerDoc(d)"
          >
            <Trash2 class="h-4.5 w-4.5" />
          </button>
        </li>
      </ul>
    </section>

    <!-- ================= Fotos ================= -->
    <section class="rounded-card bg-white p-6 shadow-card">
      <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Fotos do serviço</h2>

      <p v-if="!props.serviceOrder" class="mt-3 text-sm text-slate-500">
        As fotos pertencem a uma ordem de serviço, e esta aeronave não tem nenhuma aberta.
      </p>

      <template v-else>
        <form class="mt-5 grid gap-4 border-b border-slate-100 pb-6 sm:grid-cols-2" @submit.prevent="enviarFoto">
          <Field id="f-cap" v-model="foto.service_photo.caption" label="Legenda" required :error="foto.errors.caption" />
          <Field id="f-date" v-model="foto.service_photo.taken_on" label="Data" type="date" hint="Em branco usa a data de hoje." />
          <div class="sm:col-span-2">
            <label for="f-img" class="block text-sm font-medium text-up-navy">Imagem</label>
            <input
              id="f-img"
              type="file"
              accept="image/*"
              required
              class="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2 text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-up-navy file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white"
              @input="foto.image = $event.target.files[0]"
            >
            <p v-if="foto.errors.image" class="mt-1 text-xs text-up-orange-dark">{{ foto.errors.image }}</p>
          </div>
          <div class="sm:col-span-2">
            <button
              type="submit"
              :disabled="foto.processing"
              class="flex items-center gap-2 rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark disabled:opacity-60"
            >
              <Upload class="h-4 w-4" aria-hidden="true" />
              {{ foto.processing ? `Enviando… ${foto.progress?.percentage ?? 0}%` : 'Enviar foto' }}
            </button>
          </div>
        </form>

        <p v-if="!props.photos.length" class="mt-5 text-sm text-slate-500">Nenhuma foto enviada nesta ordem de serviço.</p>

        <ul v-else class="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <li v-for="f in props.photos" :key="f.id" class="overflow-hidden rounded-lg border border-slate-100">
            <img v-if="f.url" :src="f.url" :alt="f.caption" class="aspect-[4/3] w-full bg-slate-100 object-cover">
            <span v-else class="flex aspect-[4/3] items-center justify-center bg-slate-100" role="img" :aria-label="`${f.caption}: sem imagem`">
              <ImageIcon class="h-6 w-6 text-slate-400" aria-hidden="true" />
            </span>
            <div class="flex items-start gap-1 px-3 py-2">
              <div class="min-w-0">
                <p class="truncate text-xs font-medium text-up-navy">{{ f.caption }}</p>
                <p class="truncate text-[11px] text-slate-500">
                  {{ f.at }}<span v-if="f.uploadedBy"> · {{ f.uploadedBy }}</span>
                </p>
              </div>
              <button
                type="button"
                class="ml-auto shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:text-up-orange"
                :aria-label="`Remover foto ${f.caption}`"
                @click="removerFoto(f)"
              >
                <Trash2 class="h-4 w-4" />
              </button>
            </div>
          </li>
        </ul>
      </template>
    </section>
  </div>
</template>
